<script setup>
import { defineProps, computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

// Bloom: render a Front-style participant string for collapsed activity
// runs. 1 → "Alice"; 2 → "Alice and Bob"; 3+ → "Alice, Bob and N others".
const formatParticipantNames = names => {
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) {
    return t('CONVERSATION.ACTIVITY_GROUP_AND', { a: names[0], b: names[1] });
  }
  return t('CONVERSATION.ACTIVITY_GROUP_AND_OTHERS', {
    a: names[0],
    b: names[1],
    count: names.length - 2,
  });
};

// Bloom: getInReplyToMessage falls back to scanning the currently-
// selected conversation's full message list (which the store keeps
// hot) before hitting the API. Without this binding the function
// threw "currentChat is not defined" any time the in-reply-to id
// wasn't already in props.messages.
const currentChat = useMapGetter('getSelectedChat');

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: ['content_attributes.translations'],
  });
});

// Bloom: groups consecutive activity messages so a string of
// "Marked resolved" / "Reopened" / "Assigned to X" rows collapses into
// a single expandable strip. Front does this; without it long
// conversations turn into walls of grey activity chrome.
//
// Tuning:
//   ACTIVITY_GROUP_THRESHOLD = 3 — only collapse runs of 3+. Two-
//     activity runs (e.g. "assigned + status to open") stay inline
//     because they're informative at a glance.
//   The trailing run (newest activities, after the last real message)
//     auto-expands so the agent sees what just happened. Older runs
//     start collapsed.
const ACTIVITY_TYPE = MESSAGE_TYPES.ACTIVITY;
const ACTIVITY_GROUP_THRESHOLD = 3;

const expandedActivityGroups = ref(new Set());
const toggleActivityGroup = key => {
  if (expandedActivityGroups.value.has(key)) {
    expandedActivityGroups.value.delete(key);
  } else {
    expandedActivityGroups.value.add(key);
  }
  // Reactivity nudge — Set mutation isn't reactive on its own.
  expandedActivityGroups.value = new Set(expandedActivityGroups.value);
};

const isTrailingRun = (msgs, j) => j === msgs.length;

const displayItems = computed(() => {
  const items = [];
  const msgs = allMessages.value;
  let i = 0;
  while (i < msgs.length) {
    const cur = msgs[i];
    if (cur.messageType !== ACTIVITY_TYPE) {
      items.push({ kind: 'message', message: cur });
      i += 1;
    } else {
      const run = [cur];
      let j = i + 1;
      while (j < msgs.length && msgs[j].messageType === ACTIVITY_TYPE) {
        run.push(msgs[j]);
        j += 1;
      }
      if (run.length < ACTIVITY_GROUP_THRESHOLD) {
        // Below threshold — render inline. Two-activity runs stay
        // visible so the common "assignee + status change" pair
        // doesn't hide under a clickable strip.
        run.forEach(m => items.push({ kind: 'message', message: m }));
      } else {
        items.push({
          kind: 'activity-group',
          key: `act-${cur.id}`,
          messages: run,
          autoExpand: isTrailingRun(msgs, j),
        });
      }
      i = j;
    }
  }
  return items;
});

// Auto-expand the trailing run. Watching displayItems instead of seeding
// expandedActivityGroups directly because the trailing run can change as
// new messages arrive (real-time). Sync each render.
watch(
  displayItems,
  items => {
    const next = new Set(expandedActivityGroups.value);
    items
      .filter(it => it.kind === 'activity-group' && it.autoExpand)
      .forEach(it => next.add(it.key));
    if (next.size !== expandedActivityGroups.value.size) {
      expandedActivityGroups.value = next;
    }
  },
  { immediate: true }
);

// Build a human summary for the collapsed activity strip. Examples:
//   "5 changes by Alice"
//   "8 changes by Alice, Bob"
//   "12 changes by Alice, Bob + 2 others"
const summarizeActivityGroup = group => {
  const names = group.messages
    .map(
      m =>
        m.sender?.name ||
        m.sender?.available_name ||
        m.contentAttributes?.performedBy ||
        null
    )
    .filter(Boolean);
  return Array.from(new Set(names));
};

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-6 bg-n-surface-1">
    <slot name="beforeAll" />
    <template
      v-for="(item, index) in displayItems"
      :key="item.kind === 'message' ? item.message.id : item.key"
    >
      <template v-if="item.kind === 'message'">
        <slot
          v-if="firstUnreadId && item.message.id === firstUnreadId"
          name="unreadBadge"
        />
        <Message
          v-bind="item.message"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(item.message)"
          :group-with-next="shouldGroupWithNext(index, allMessages)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          :parent-message-id="item.message.parentMessageId"
          :parent-message="item.message.parentMessage"
          data-clarity-mask="True"
          @retry="emit('retry', item.message)"
        />
      </template>
      <li v-else :key="item.key" class="my-2">
        <!-- Collapsed activity group: shows participants + count + latest.
             Click expands the rest inline. Front-style activity strip. -->
        <button
          v-if="!expandedActivityGroups.has(item.key)"
          type="button"
          :aria-expanded="false"
          :aria-controls="`${item.key}-detail`"
          class="w-full flex items-center gap-2 text-xs rounded-md py-1.5 px-3 transition-colors hover:bg-n-alpha-2 text-bloom-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bloom-purple/40"
          @click="toggleActivityGroup(item.key)"
        >
          <span aria-hidden="true" class="shrink-0">·</span>
          <span class="flex-1 truncate text-left">
            <span class="font-medium">
              {{
                $t('CONVERSATION.ACTIVITY_GROUP_SUMMARY', {
                  count: item.messages.length,
                })
              }}
            </span>
            <span v-if="summarizeActivityGroup(item).length" class="opacity-80">
              {{
                $t('CONVERSATION.ACTIVITY_GROUP_BY', {
                  names: formatParticipantNames(summarizeActivityGroup(item)),
                })
              }}
            </span>
          </span>
          <span
            class="shrink-0 text-xxs px-1.5 py-0.5 rounded-full font-medium bg-bloom-grey-soft text-bloom-text-tertiary"
          >
            {{ $t('CONVERSATION.SHOW') }}
          </span>
        </button>
        <div v-else :id="`${item.key}-detail`">
          <Message
            v-for="m in item.messages"
            :key="m.id"
            v-bind="m"
            :is-email-inbox="isAnEmailChannel"
            :in-reply-to="getInReplyToMessage(m)"
            :group-with-next="false"
            :inbox-supports-reply-to="inboxSupportsReplyTo"
            :current-user-id="currentUserId"
            data-clarity-mask="True"
            @retry="emit('retry', m)"
          />
          <button
            type="button"
            :aria-expanded="true"
            :aria-controls="`${item.key}-detail`"
            class="w-full text-center text-xxs py-1 hover:underline text-bloom-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bloom-purple/40 rounded"
            @click="toggleActivityGroup(item.key)"
          >
            {{ $t('CONVERSATION.ACTIVITY_GROUP_COLLAPSE') }}
          </button>
        </div>
      </li>
    </template>
    <slot name="after" />
  </ul>
</template>
